import type { EditableGraph } from "./lasso.ts";
import type { LngLat } from "./types.ts";

export interface DissolvedGraph extends EditableGraph {
  /** Old point index to the compacted index, or -1 when that point was removed. */
  pointMap: Int32Array;
  /**
   * For each new segment, the old segment indexes collapsed into that line.
   * Empty when the segment survived. A bridge lists the lines it replaced.
   */
  collapsedFrom: number[][];
}

/**
 * Remove the listed points. A point with exactly two lines is dissolved:
 * it goes away and its two neighbors become one line, unless that line
 * already exists or would be a self-loop. A run of consecutive dissolved
 * points collapses to one line between the surviving outer neighbors.
 * An endpoint, a junction, or an isolated point is removed with the lines
 * that touched it. The input graph is left unchanged.
 */
export function dissolvePoints(graph: EditableGraph, remove: readonly number[]): DissolvedGraph {
  const count = graph.points.length;
  const drop = new Uint8Array(count);
  for (const index of remove) {
    if (index >= 0 && index < count) drop[index] = 1;
  }

  const adj: Array<Array<{ to: number; seg: number }>> = Array.from({ length: count }, () => []);
  for (let seg = 0; seg < graph.segments.length; seg++) {
    const segment = graph.segments[seg];
    if (!segment) continue;
    const a = segment[0];
    const b = segment[1];
    if (a < 0 || b < 0 || a >= count || b >= count) continue;
    adj[a].push({ to: b, seg });
    adj[b].push({ to: a, seg });
  }

  const dissolve = new Uint8Array(count);
  const hard = new Uint8Array(count);
  for (let i = 0; i < count; i++) {
    if (!drop[i]) continue;
    if (adj[i]?.length === 2) dissolve[i] = 1;
    else hard[i] = 1;
  }

  const seen = new Uint8Array(count);
  const bridges: Array<{ a: number; b: number; segs: number[] }> = [];
  for (let i = 0; i < count; i++) {
    if (!dissolve[i] || seen[i]) continue;
    seen[i] = 1;
    const neighbors = adj[i] ?? [];
    const left = walkEnd(i, neighbors[0], count, dissolve, hard, adj, seen);
    const right = walkEnd(i, neighbors[1], count, dissolve, hard, adj, seen);
    if (left.end >= 0 && right.end >= 0 && left.end !== right.end) {
      bridges.push({ a: left.end, b: right.end, segs: left.segs.concat(right.segs) });
    }
  }

  const pointMap = new Int32Array(count).fill(-1);
  const points: LngLat[] = [];
  for (let i = 0; i < count; i++) {
    if (drop[i]) continue;
    const at = graph.points[i];
    if (!at) continue;
    pointMap[i] = points.length;
    points.push([at[0], at[1]]);
  }

  const segments: Array<[number, number]> = [];
  const collapsedFrom: number[][] = [];
  const seenEdge = new Set<string>();
  const keep = (a: number, b: number, from: number[]) => {
    const na = pointMap[a] ?? -1;
    const nb = pointMap[b] ?? -1;
    if (na < 0 || nb < 0 || na === nb) return;
    const lo = Math.min(na, nb);
    const hi = Math.max(na, nb);
    const key = `${lo}:${hi}`;
    if (seenEdge.has(key)) return;
    seenEdge.add(key);
    segments.push([lo, hi]);
    collapsedFrom.push(from);
  };
  for (const [a, b] of graph.segments) {
    if (drop[a] || drop[b]) continue;
    keep(a, b, []);
  }
  for (const bridge of bridges) keep(bridge.a, bridge.b, bridge.segs);

  const mappedStart = graph.start == null ? -1 : (pointMap[graph.start] ?? -1);
  const selection: number[] = [];
  const selected = new Set<number>();
  for (const index of graph.selection) {
    const next = pointMap[index] ?? -1;
    if (next < 0 || selected.has(next)) continue;
    selected.add(next);
    selection.push(next);
  }
  selection.sort((a, b) => a - b);
  return {
    points,
    segments,
    start: mappedStart >= 0 ? mappedStart : null,
    selection,
    pointMap,
    collapsedFrom,
  };
}

/**
 * Walk from a dissolved point until a point that stays. A hard-deleted
 * point, a cycle, or a self-neighbor ends the walk with no survivor.
 */
function walkEnd(
  start: number,
  step: { to: number; seg: number } | undefined,
  count: number,
  dissolve: Uint8Array,
  hard: Uint8Array,
  adj: Array<Array<{ to: number; seg: number }>>,
  seen: Uint8Array,
): { end: number; segs: number[] } {
  if (!step) return { end: -1, segs: [] };
  const segs = [step.seg];
  let prev = start;
  let cur = step.to;
  for (let guard = 0; guard <= count; guard++) {
    if (cur < 0 || cur >= count || cur === start) return { end: -1, segs: [] };
    if (hard[cur]) return { end: -1, segs: [] };
    if (!dissolve[cur]) return { end: cur, segs };
    seen[cur] = 1;
    const neighbors = adj[cur];
    if (!neighbors || neighbors.length !== 2) return { end: -1, segs: [] };
    const next = neighbors[0]?.to === prev ? neighbors[1] : neighbors[0];
    if (!next) return { end: -1, segs: [] };
    segs.push(next.seg);
    prev = cur;
    cur = next.to;
  }
  return { end: -1, segs: [] };
}
